import { useState, useEffect, useCallback } from "react";
import { useSmoothScroll } from "../../../hooks/useScrollAnimation";
import { ArrowLeft, ArrowRight, ShieldCheck, Factory, Clock3 } from "lucide-react";

const slides = [
  {
    image: "https://storage.readdy-site.link/project_files/ca0597d3-e4ee-4889-8ae5-f7591fdfc85d/61d6f45f-86ef-4ef7-a233-d6a9bd37ad24_slider.png?v=b333618e5107e5b17d6bad23801eacb8",
    tag: "Çelik Konstrüksiyon Uzmanı",
    title: ["Endüstriyel", "Yapı Çözümleri"],
    description:
      "Fabrika, depo ve anahtar teslim projelerde mühendislik gücümüzle yanınızdayız. 15+ yıllık deneyimimizle Türkiye'nin her yerine hizmet sunuyoruz.",
    ctaPrimary: { text: "Teklif Al", target: "#contact" },
    ctaSecondary: { text: "Projeleri İncele", target: "#projects" },
  },
  {
    image: "https://storage.readdy-site.link/project_files/ca0597d3-e4ee-4889-8ae5-f7591fdfc85d/42badd9a-b26f-4702-9050-65c5dace6b88_slider2.jpg?v=efbfc4fcef91b5af8795fd65751231b3",
    tag: "Mühendislik & İmalat",
    title: ["Güçlü", "Yapı Tasarımları"],
    description:
      "Yüksek kaliteli çelik konstrüksiyon, özel projelendirme ve anahtar teslim çözümlerle işletmenizin ihtiyaçlarına özel tasarımlar sunuyoruz.",
    ctaPrimary: { text: "Hizmetlerimiz", target: "#services" },
    ctaSecondary: { text: "Bize Ulaşın", target: "#contact" },
  },
  {
    image: "https://storage.readdy-site.link/project_files/ca0597d3-e4ee-4889-8ae5-f7591fdfc85d/bb465f6c-1413-44c6-a15d-a13ad62221de_slider-3.jpg?v=4df2214c74d22607a861982602e357d4",
    tag: "Anahtar Teslim Projeler",
    title: ["Fabrika", "ve Depo Çözümleri"],
    description:
      "Türkiye'nin dört bir yanında anahtar teslim endüstriyel yapılar inşa ediyoruz. Lojistik merkezlerinden üretim tesislerine kadar kapsamlı çözümler.",
    ctaPrimary: { text: "Projelerimiz", target: "#projects" },
    ctaSecondary: { text: "Teklif Al", target: "#contact" },
  },
  {
    image: "https://storage.readdy-site.link/project_files/ca0597d3-e4ee-4889-8ae5-f7591fdfc85d/5cade05a-acc6-4c8e-bfec-a5c5637e616a_slider4.jpg?v=4aa88c2142bdb4697b50384d78f9724e",
    tag: "Yüksek Standart",
    title: ["Kaliteli", "Çelik İmalat"],
    description:
      "Modern üretim tesisimizde ISO standartlarında çelik işleme, hassas kesim ve uzun ömürlü koruyucu kaplama ile en yüksek kaliteyi garanti ediyoruz.",
    ctaPrimary: { text: "Hakkımızda", target: "#about" },
    ctaSecondary: { text: "İletişim", target: "#contact" },
  },
];

export default function Hero() {
  const scrollTo = useSmoothScroll();
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState<boolean[]>(slides.map(() => false));
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (animating || index === current) return;
      setAnimating(true);
      setCurrent(index);
      setTimeout(() => setAnimating(false), 700);
    },
    [animating, current]
  );

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const handleImageLoad = (idx: number) => {
    setLoaded((prev) => {
      const copy = [...prev];
      copy[idx] = true;
      return copy;
    });
  };

  return (
    <section id="hero" className="relative min-h-[100dvh] flex items-center overflow-hidden">
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            idx === current ? "opacity-100 z-[1]" : "opacity-0 z-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title.join(" ")}
            className={`w-full h-full object-cover transition-opacity duration-700 ${
              loaded[idx] ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => handleImageLoad(idx)}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/65 to-navy/30" />
        </div>
      ))}

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 w-full py-32 md:py-40">
        {slides.map((slide, idx) => (
          <article
            key={idx}
            className={`max-w-3xl transition-all duration-700 ease-in-out ${
              idx === current
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 translate-y-4 pointer-events-none absolute"
            }`}
          >
            <div className="mb-4 md:mb-6">
              <span className="inline-flex items-center rounded-full bg-white/8 border border-white/20 px-4 py-2 font-body text-xs md:text-sm text-copper font-semibold tracking-wider uppercase">
                {slide.tag}
              </span>
            </div>

            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.04] tracking-tight mb-5 md:mb-6">
              {slide.title[0]}
              <br />
              {slide.title[1]}
            </h1>

            <p className="font-body text-base md:text-lg lg:text-xl text-white/85 leading-relaxed max-w-2xl mb-8 md:mb-10">
              {slide.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 md:gap-5">
              <button
                onClick={() => scrollTo(slide.ctaPrimary.target)}
                className="bg-copper hover:bg-copper-dark text-white font-heading font-semibold text-base md:text-lg px-8 py-4 rounded-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl whitespace-nowrap shadow-[0_8px_24px_rgba(196,122,44,0.35)]"
              >
                {slide.ctaPrimary.text}
              </button>
              <button
                onClick={() => scrollTo(slide.ctaSecondary.target)}
                className="border-2 border-white/35 hover:border-copper text-white font-heading font-semibold text-base md:text-lg px-8 py-4 rounded-md transition-all duration-300 hover:bg-white/5 hover:-translate-y-1 whitespace-nowrap"
              >
                {slide.ctaSecondary.text}
              </button>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-white/90 font-body text-xs md:text-sm">
                <ShieldCheck size={15} className="text-copper" />
                ISO odaklı kalite
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-white/90 font-body text-xs md:text-sm">
                <Factory size={15} className="text-copper" />
                Anahtar teslim üretim
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-white/90 font-body text-xs md:text-sm">
                <Clock3 size={15} className="text-copper" />
                Zamanında teslim
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              idx === current ? "bg-copper w-8" : "bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>

      {/* Arrows */}
      <button
        onClick={() => goTo((current - 1 + slides.length) % slides.length)}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-300"
        aria-label="Önceki slide"
      >
        <ArrowLeft size={20} />
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-300"
        aria-label="Sonraki slide"
      >
        <ArrowRight size={20} />
      </button>
    </section>
  );
}
